'use client'

import { useEffect } from 'react'

const HIGHLIGHT = ['ring-2', 'ring-brand', 'ring-offset-2', 'ring-offset-background']

/**
 * When the page is opened with `#<targetId>` (e.g. the nav PlanBadge links to
 * /settings#subscription), reliably scroll that element into view and flash a
 * brief ring around it — so it's obvious you landed on the billing section, not
 * the top of the page. App Router's native hash scroll can miss a below-the-fold
 * card that mounts after navigation, so we do it explicitly here, and also on
 * `hashchange` (clicking the badge again while already on /settings).
 */
export default function ScrollHashHighlight({ targetId }: { targetId: string }) {
  useEffect(() => {
    let removeTimer: number | undefined

    const run = () => {
      if (window.location.hash !== `#${targetId}`) return
      const el = document.getElementById(targetId)
      if (!el) return
      // Defer the scroll until layout settles (post-mount).
      requestAnimationFrame(() => {
        window.setTimeout(() => el.scrollIntoView?.({ behavior: 'smooth', block: 'start' }), 0)
      })
      el.classList.add(...HIGHLIGHT)
      if (removeTimer) window.clearTimeout(removeTimer)
      removeTimer = window.setTimeout(() => el.classList.remove(...HIGHLIGHT), 2200)
    }

    run()
    window.addEventListener('hashchange', run)
    return () => {
      window.removeEventListener('hashchange', run)
      if (removeTimer) window.clearTimeout(removeTimer)
    }
  }, [targetId])

  return null
}
